<template>
  <div class="modelos-page">
    <header class="catalog-header">
      <h1>Catálogo de Modelos — Configuraciones</h1>
      <p v-if="families.length">{{ families.length }} modelos disponibles</p>
    </header>

    <div class="catalog-controls">
      <input
        v-model="search"
        type="text"
        placeholder="Buscar por código o modelo..."
        class="catalog-input"
      />
      <select v-model="serieFilter" class="catalog-select">
        <option value="">Todas las series</option>
        <option v-for="s in seriesList" :key="s" :value="s">{{ s }}</option>
      </select>
    </div>

    <div class="catalog-count">
      <span v-if="!loading">{{ filtered.length }} resultado{{ filtered.length !== 1 ? "s" : "" }}</span>
    </div>

    <div v-if="loading" class="catalog-loading">Cargando modelos...</div>
    <div v-else-if="error" class="catalog-error">{{ error }}</div>
    <div v-else-if="filtered.length === 0" class="catalog-empty">No se encontraron resultados.</div>
    <div v-else class="catalog-grid">
      <div
        v-for="fam in filtered"
        :key="fam.catalogCode"
        class="catalog-card"
        @click="openFamily(fam)"
      >
        <img
          v-if="fam.imageUrl"
          :src="fam.imageUrl"
          :alt="fam.catalogCode"
          loading="lazy"
          class="catalog-card-img"
        />
        <div v-else class="catalog-card-img catalog-card-img--empty">Sin imagen</div>
        <div class="catalog-card-body">
          <div class="catalog-code">{{ fam.catalogCode }}</div>
          <span class="catalog-serie">{{ fam.serie }}</span>
          <div class="catalog-model">{{ fam.family }}</div>
        </div>
      </div>
    </div>

    <!-- Modal vehículos -->
    <div v-if="activeFamily" class="catalog-lightbox" @click.self="activeFamily = null">
      <div class="catalog-lightbox-box">
        <button class="catalog-lightbox-close" @click="activeFamily = null">&times;</button>
        <div class="catalog-lightbox-top">
          <img
            v-if="activeFamily.imageUrl"
            :src="activeFamily.imageUrl"
            :alt="activeFamily.catalogCode"
            class="catalog-lightbox-img"
          />
          <div class="catalog-lightbox-info">
            <div class="catalog-code catalog-code--white">{{ activeFamily.catalogCode }}</div>
            <div class="catalog-lightbox-family">{{ activeFamily.family }}</div>
            <span class="catalog-serie catalog-serie--light">{{ activeFamily.serie }}</span>
          </div>
        </div>

        <div class="catalog-vehicles">
          <div v-if="loadingVehicles" class="catalog-loading">Cargando vehículos...</div>
          <div v-else-if="activeVehicles.length === 0" class="catalog-empty">Sin vehículos asignados.</div>
          <table v-else class="catalog-vtable">
            <thead>
              <tr>
                <th>Año</th>
                <th>Marca</th>
                <th>Modelo</th>
                <th>Motor</th>
                <th>Región</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="v in activeVehicles" :key="v.id">
                <td>{{ v.year }}</td>
                <td>{{ v.make }}</td>
                <td>{{ v.model }}</td>
                <td>{{ [v.engineName, v.engineLiter ? v.engineLiter + 'L' : null].filter(Boolean).join(' ') || '—' }}</td>
                <td>{{ v.region || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import camelize from "camelize";
const APP_VERSION = require("../../../package.json").version;

const CATALOG_BASE_URL = process.env.VUE_APP_PUBLIC_CATALOG_API;

const publicHttp = axios.create({
  baseURL: CATALOG_BASE_URL,
  headers: { "X-App-Version": APP_VERSION }
});

export default {
  name: "ModelosCatalogo",
  data() {
    return {
      families: [],
      loading: true,
      error: null,
      search: "",
      serieFilter: "",
      activeFamily: null,
      activeVehicles: [],
      loadingVehicles: false
    };
  },
  computed: {
    seriesList() {
      return [...new Set(this.families.map(f => f.serie))].sort();
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      const serie = this.serieFilter;
      return this.families.filter(f => {
        const matchQ =
          !q ||
          f.catalogCode.toLowerCase().includes(q) ||
          f.family.toLowerCase().includes(q);
        const matchSerie = !serie || f.serie === serie;
        return matchQ && matchSerie;
      });
    }
  },
  async mounted() {
    try {
      const res = await publicHttp.get("vehicle_management/families", {
        params: { active: 1 }
      });
      this.families = camelize(res.data || []);
    } catch (e) {
      this.error = "Error al cargar el catálogo. Intente más tarde.";
      console.error(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async openFamily(fam) {
      this.activeFamily = fam;
      this.activeVehicles = [];
      this.loadingVehicles = true;
      try {
        const res = await publicHttp.get("vehicle_management/family_vehicles", {
          params: { catalog_code: fam.catalogCode }
        });
        this.activeVehicles = camelize(res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingVehicles = false;
      }
    }
  }
};
</script>

<style scoped>
:root {
  --accent: #c8102e;
  --accent-dark: #9c0c22;
}

.modelos-page {
  min-height: 100vh;
  background: #f5f6f8;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
  color: #1b1e23;
}

.catalog-header {
  background: #c8102e;
  color: #fff;
  padding: 20px 24px;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.catalog-header h1 {
  margin: 0 0 4px;
  font-size: 20px;
}
.catalog-header p {
  margin: 0;
  font-size: 13px;
  opacity: 0.9;
}

.catalog-controls {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #e2e4e9;
  position: sticky;
  top: 76px;
  z-index: 9;
}

.catalog-input,
.catalog-select {
  padding: 10px 12px;
  border: 1px solid #e2e4e9;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  color: #1b1e23;
}
.catalog-input {
  flex: 1;
  min-width: 200px;
}
.catalog-select {
  min-width: 180px;
}

.catalog-count {
  padding: 10px 24px 0;
  font-size: 13px;
  color: #6b7280;
}

.catalog-loading,
.catalog-error,
.catalog-empty {
  padding: 60px 24px;
  text-align: center;
  color: #6b7280;
}
.catalog-error {
  color: #c8102e;
}

.catalog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  padding: 16px 24px 40px;
}

.catalog-card {
  background: #fff;
  border: 1px solid #e2e4e9;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s;
}
.catalog-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.catalog-card-img {
  width: 100%;
  height: 160px;
  object-fit: contain;
  background: #fafafa;
  border-bottom: 1px solid #e2e4e9;
}
.catalog-card-img--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #9ca3af;
}

.catalog-card-body {
  padding: 12px 14px;
}

.catalog-code {
  font-weight: 700;
  font-size: 16px;
  color: #9c0c22;
}
.catalog-code--white {
  color: #fff;
  font-size: 22px;
}

.catalog-serie {
  display: inline-block;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #fff;
  background: #3a3f4b;
  padding: 2px 8px;
  border-radius: 12px;
}
.catalog-serie--light {
  background: rgba(255,255,255,0.2);
  margin-top: 8px;
}

.catalog-model {
  margin-top: 8px;
  font-size: 13px;
  color: #1b1e23;
  line-height: 1.4;
}

/* Lightbox */
.catalog-lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.catalog-lightbox-box {
  background: #1b1e23;
  border-radius: 12px;
  max-width: 860px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 24px;
}

.catalog-lightbox-close {
  position: absolute;
  top: 14px;
  right: 18px;
  color: #fff;
  font-size: 28px;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 1;
}

.catalog-lightbox-top {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.catalog-lightbox-img {
  max-width: 280px;
  max-height: 200px;
  object-fit: contain;
  background: #fff;
  border-radius: 8px;
  flex-shrink: 0;
}

.catalog-lightbox-info {
  color: #fff;
  padding-top: 8px;
}

.catalog-lightbox-family {
  font-size: 15px;
  color: #d1d5db;
  margin-top: 8px;
}

/* Vehicles table */
.catalog-vehicles {
  border-top: 1px solid rgba(255,255,255,0.1);
  padding-top: 16px;
}

.catalog-vtable {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #e5e7eb;
}
.catalog-vtable thead th {
  text-align: left;
  padding: 8px 10px;
  background: rgba(255,255,255,0.08);
  font-weight: 600;
  color: #9ca3af;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.catalog-vtable tbody tr {
  border-bottom: 1px solid rgba(255,255,255,0.05);
  transition: background 0.15s;
}
.catalog-vtable tbody tr:hover {
  background: rgba(255,255,255,0.05);
}
.catalog-vtable tbody td {
  padding: 8px 10px;
}
</style>
